import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin } from "lucide-react";
import { motion } from "framer-motion";
import type { LinkTreeCta, LinkTreeVenue } from "@/data/linkTreeVenues";
import { trackEvent } from "@/lib/amplitude";
import { trackWaiterEvent } from "@/services/waiterTracking";
import LinkTreeCtaButton from "./LinkTreeCtaButton";
import pastaInCorsoLogo from "@/assets/links-page/pastaincorso_logo.png";
import pastaInCorsoSeparator from "@/assets/links-page/pastaincorso_separator.svg";

interface LinkTreeCardProps {
  venue: LinkTreeVenue;
  isTag: boolean;
  waiterId: number | null;
}

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
  },
};

export default function LinkTreeCard({ venue, isTag, waiterId }: LinkTreeCardProps) {
  const navigate = useNavigate();
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    if (waiterId !== null && !hasTrackedPageView.current) {
      hasTrackedPageView.current = true;
      trackWaiterEvent(waiterId, "page_view");
    }
  }, [waiterId]);

  function handleCtaClick(cta: LinkTreeCta): void {
    trackEvent("landing_cta_click", {
      venue_slug: venue.slug,
      venue_name: venue.name,
      cta_type: cta.type,
      is_tag: isTag,
      waiter_id: waiterId,
    });

    if (waiterId !== null) {
      trackWaiterEvent(
        waiterId,
        cta.type as "google" | "tripadvisor" | "instagram" | "bestiebite" | "menu",
      );
    }

    if (cta.isExternal) {
      window.open(cta.url, "_blank", "noopener,noreferrer");
    } else {
      localStorage.setItem("placeRef", venue.placeId);
      localStorage.setItem("placeRefTimestamp", Date.now().toString());
      if (waiterId !== null) {
        sessionStorage.setItem("waiterRef", waiterId.toString());
      }
      navigate("/");
    }
  }

  return (
    <motion.div
      className="flex flex-col items-center px-8 pb-12 pt-14"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Logo */}
      <motion.img
        src={pastaInCorsoLogo}
        alt={venue.name}
        className="mb-4 h-36 w-36 object-contain"
        variants={itemVariants}
      />

      {/* Header */}
      <motion.div className="text-center" variants={itemVariants}>
        <h1
          className="text-3xl font-bold tracking-tight text-stone-900"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {venue.name}
        </h1>
        <p className="mt-3 flex items-center justify-center gap-1.5 text-sm text-stone-500">
          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
          {venue.address}
        </p>
      </motion.div>

      {/* Divider */}
      <motion.img
        src={pastaInCorsoSeparator}
        alt=""
        className="my-8 w-48 opacity-30"
        variants={itemVariants}
      />

      {/* CTA Buttons */}
      <div className="flex w-full flex-col gap-4">
        {venue.ctas.map((cta, index) => {
          const isMenuFollowedByOther =
            cta.type === "menu" &&
            index < venue.ctas.length - 1 &&
            venue.ctas[index + 1].type !== "menu";
          return (
            <motion.div key={cta.type} variants={itemVariants}>
              <LinkTreeCtaButton cta={cta} onClick={handleCtaClick} />
              {isMenuFollowedByOther && (
                <div className="mt-4 flex items-center gap-3">
                  <div className="h-px flex-1 bg-stone-300/50" />
                  <span className="text-[11px] uppercase tracking-widest text-stone-600">
                    Lascia una recensione
                  </span>
                  <div className="h-px flex-1 bg-stone-300/50" />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}
